import { enqueueSnackbar } from 'notistack';
import { useRef, useState, useEffect } from 'react';

import Button from '@/components/Button/Button';
import { Switch } from '@/components/Switch';
import { ERoleAccess } from '@/enum';
import useAccess from '@/hooks/useAccess';
import { useFormatText } from '@/hooks/useFormatText';
import {
  getMaintenanceMode,
  updateMaintenanceMode,
} from '@/service/settingsServices';
const General = () => {
  const { formatText } = useFormatText();
  const { hasAccess } = useAccess();
  const canAccessMaintenanceMode = hasAccess(ERoleAccess.MAINTENANCE_MODE);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const initialMaintenanceMode = useRef(false);
  const [isMaintenanceLoading, setIsMaintenanceLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (canAccessMaintenanceMode) {
      fetchMaintenanceMode();
    }
  }, [canAccessMaintenanceMode]);

  const fetchMaintenanceMode = async () => {
    setIsMaintenanceLoading(true);
    try {
      const response = await getMaintenanceMode();
      if (response?.status) {
        const enabled = !!response?.data?.maintenance_mode;
        setMaintenanceMode(enabled);
        initialMaintenanceMode.current = enabled;
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsMaintenanceLoading(false);
    }
  };

  const handleSave = async () => {
    if (!canAccessMaintenanceMode) return;

    setIsSaving(true);
    try {
      const response = await updateMaintenanceMode({
        is_enabled: maintenanceMode,
      });
      if (response?.status) {
        enqueueSnackbar(response?.message, { variant: 'success' });
        initialMaintenanceMode.current = maintenanceMode;
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar(formatText('common.error.generic'), {
        variant: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (!canAccessMaintenanceMode) return null;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-12.5">
        <div className="flex-1 max-w-2xl">
          <h3 className="text-base font-semibold text-neutral-primary mb-2">
            {formatText('settings.general.maintenance-mode')}
          </h3>
          <p className="text-sm text-neutral-secondary leading-5">
            {formatText('settings.general.maintenance-desc')}
          </p>
        </div>
        <div className="shrink-0">
          <Switch
            id="maintenance-mode"
            checked={maintenanceMode}
            onChange={setMaintenanceMode}
            disabled={isMaintenanceLoading}
          />
        </div>
      </div>
      <Button
        label={formatText('common.actions.save-changes')}
        variant="primary"
        onClick={handleSave}
        loading={isSaving}
        disabled={isSaving}
      />
    </div>
  );
};

export default General;
